import { useState, FormEvent } from "react";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import { Paginator } from "primereact/paginator";
import { AxiosError } from "axios";
import { DateTime } from "luxon";
import { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthContext, useDarkModeContext } from "../../contexts";
import {
  DocumentListFilterFields,
  invalidateDocumentList,
  useDocumentList,
  useStatusOptions,
  useDraftRemover,
  useDocumentDeregistrator
} from "../../api/DataAPI";
import useStrings from "../../api/StringsAPI";
import { statusTagProps } from "./statusTag";
import DarkModeSwitch from "../../components/DarkModeSwitch";
import LogoutButton from "../../components/LogoutButton";
import NewApplicationDialog from "./NewApplicationDialog";
import ApplicationDetailsDialog from "./ApplicationDetailsDialog";
import ChangeAddressDialog from "./ChangeAddressDialog";

interface DocumentRecord {
  id: number;
  taxNumber: string;
  orgName: string;
  deviceSerial: string;
  sealNumber: string;
  samModule: string;
  status: { id: string; name: string };
  lastChange: string;
  creatorUser: { username: string };
}

export default function DocumentListView() {
  const str = useStrings();
  const toast = useRef<Toast>(null);
  const queryClient = useQueryClient();
  const { darkMode } = useDarkModeContext();
  const { authInfo } = useAuthContext();
  const statusOptions = useStatusOptions();
  const deleteDraftMutation = useDraftRemover();
  const deregisterMutation = useDocumentDeregistrator();

  const [sortField, setSortField] = useState('lastChange');
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1);
  const [currentPage, setCurrentPage] = useState(0);
  const [filterInput, setFilterInput] = useState<DocumentListFilterFields>({});
  const [appliedFilters, setAppliedFilters] = useState<DocumentListFilterFields>({});

  // Dialogs
  const [showNewAppDialog, setShowNewAppDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showChangeAddressDialog, setShowChangeAddressDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentRecord | null>(null);

  const RECORDS_PER_PAGE = Number.parseInt(process.env.REACT_APP_RECORDS_PER_PAGE || "15");

  const documentListQuery = useDocumentList({
    sort: `${sortField},${sortOrder === -1 ? 'Desc' : 'Asc'}`,
    size: RECORDS_PER_PAGE,
    page: currentPage,
    filter: appliedFilters
  });

  const isDeregisterUser = authInfo.userType === 'DEREGISTER';

  const documents: DocumentRecord[] = documentListQuery.data?.data?.applicationList || [];
  const totalRecords = documentListQuery.data?.data?.count || 0;

  const onSort = (e: DataTableSortEvent) => {
    setSortField(e.sortField as string);
    setSortOrder(e.sortOrder as 1 | -1);
  };

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setAppliedFilters({ ...filterInput });
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setFilterInput({});
    setAppliedFilters({});
    setCurrentPage(0);
  };

  const onRowClick = (doc: any) => {
    setSelectedDocument(doc);
    if (doc.status?.id === 'DRAFT' || doc.status?.id === 'ERROR' || doc.status?.id === 'EXCEPTION') {
      setShowNewAppDialog(true);
    } else {
      setShowDetailsDialog(true);
    }
  };

  const handleDelete = (doc: DocumentRecord) => {
    const isDraft = doc.status?.id === 'DRAFT';
    confirmDialog({
      message: str(isDraft ? 'draftRemovalWarningSubTitle' : 'deregisterWarningSubTitle'),
      header: str(isDraft ? 'draftRemovalWarningTitle' : 'deregisterWarningTitle'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: str(isDraft ? 'delete' : 'deregister'),
      rejectLabel: str('cancel'),
      accept: () => {
        if (isDraft) {
          deleteDraftMutation.mutate(doc.id, {
            onSuccess: () => {
              toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Draft deleted' });
              invalidateDocumentList(queryClient);
            }
          });
        } else {
          deregisterMutation.mutate(doc.id, {
            onSuccess: () => {
              toast.current?.show({ severity: 'success', summary: 'Success', detail: str('deregistrationStarted') });
              invalidateDocumentList(queryClient);
            }
          });
        }
      }
    });
  };

  const handleChangeAddress = (doc: DocumentRecord) => {
    setSelectedDocument(doc);
    setShowChangeAddressDialog(true);
  };

  const statusTemplate = (rowData: DocumentRecord) => {
    return <Tag value={rowData.status?.name} {...statusTagProps(rowData.status?.id)} />;
  };

  const dateTemplate = (rowData: DocumentRecord) => {
    return rowData.lastChange ? DateTime.fromISO(rowData.lastChange).toFormat("dd/MM/yyyy HH:mm") : '-';
  };

  // Compact icon buttons so they don't make table rows taller than the text
  const ROW_BUTTON_STYLE = { width: '2rem', height: '2rem' };

  const actionsTemplate = (rowData: DocumentRecord) => {
    const canDelete = rowData.status?.id === 'DRAFT' || rowData.status?.id === 'FINISHED' || rowData.status?.id === 'DEREGISTER_ERROR';
    const canChangeAddress = rowData.status?.id === 'FINISHED' && !isDeregisterUser;

    return (
      <div style={{ display: 'flex', gap: 5 }}>
        {canChangeAddress && (
          <Button icon="pi pi-home" rounded text severity="info" style={ROW_BUTTON_STYLE} onClick={(e) => { e.stopPropagation(); handleChangeAddress(rowData); }} tooltip={str('changeAddress')} />
        )}
        {canDelete && (
          <Button icon="pi pi-trash" rounded text severity="danger" style={ROW_BUTTON_STYLE} onClick={(e) => { e.stopPropagation(); handleDelete(rowData); }} tooltip={rowData.status?.id === 'DRAFT' ? str('delete') : str('deregister')} />
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column', background: darkMode ? '#1A1A1A' : 'var(--surface-ground)', boxSizing: 'border-box' }}>
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        {!isDeregisterUser && (
          <Button label={str('newApplication')} icon="pi pi-plus" onClick={() => { setSelectedDocument(null); setShowNewAppDialog(true); }} />
        )}
        <div style={{ display: 'flex', gap: 10, marginLeft: 'auto' }}>
          <DarkModeSwitch />
          <LogoutButton />
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={onSearch} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <InputText
          value={filterInput.taxNumber || ''}
          onChange={(e) => setFilterInput({ ...filterInput, taxNumber: e.target.value })}
          placeholder={str('taxNumber')}
          style={{ width: 180 }}
        />
        <InputText
          value={filterInput.deviceSerial || ''}
          onChange={(e) => setFilterInput({ ...filterInput, deviceSerial: e.target.value })}
          placeholder={str('deviceSerial')}
          style={{ width: 180 }}
        />
        <InputText
          value={filterInput.sealNumber || ''}
          onChange={(e) => setFilterInput({ ...filterInput, sealNumber: e.target.value })}
          placeholder={str('sealNumber')}
          style={{ width: 180 }}
        />
        <InputText
          value={filterInput.samModule || ''}
          onChange={(e) => setFilterInput({ ...filterInput, samModule: e.target.value })}
          placeholder={str('samModule')}
          style={{ width: 180 }}
        />
        <Dropdown
          value={(filterInput as any).status}
          options={statusOptions.data?.data || []}
          onChange={(e) => setFilterInput({ ...filterInput, status: e.value } as any)}
          optionLabel="name"
          optionValue="id"
          placeholder={str('status')}
          showClear
          style={{ width: 180 }}
        />
        <Button type="submit" icon="pi pi-search" />
        <Button type="button" icon="pi pi-times" severity="secondary" onClick={clearFilters} />
      </form>

      {/* Loading / Error / Table */}
      {documentListQuery.isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
          <ProgressSpinner />
        </div>
      )}

      {documentListQuery.isError && (
        <Message severity="error" text={`${str('couldNotFetchData')}: ${(documentListQuery.error as AxiosError)?.message}`} />
      )}

      {documentListQuery.isSuccess && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <DataTable
              value={documents}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={onSort}
              onRowClick={(e) => onRowClick(e.data)}
              rowHover
              stripedRows
              size="small"
              emptyMessage="No documents found"
              scrollable
              scrollHeight="100%"
              style={{ cursor: 'pointer', height: '100%' }}
            >
              <Column field="id" header="#" sortable style={{ width: 70 }} />
              <Column field="taxNumber" header={str('taxNumber')} sortable />
              <Column field="orgName" header={str('orgName')} sortable body={(row) => row.orgName?.length > 25 ? row.orgName.substring(0, 25) + '...' : row.orgName} />
              <Column field="deviceSerial" header={str('deviceSerial')} sortable />
              <Column field="sealNumber" header={str('sealNumber')} sortable />
              <Column field="samModule" header={str('samModule')} sortable />
              <Column field="status" header={str('status')} body={statusTemplate} sortable />
              <Column field="lastChange" header={str('lastChange')} body={dateTemplate} sortable />
              <Column field="creatorUser.username" header={str('creatorUser')} sortable />
              <Column body={actionsTemplate} style={{ width: 100 }} />
            </DataTable>
          </div>

          {totalRecords > RECORDS_PER_PAGE && (
            <Paginator
              first={currentPage * RECORDS_PER_PAGE}
              rows={RECORDS_PER_PAGE}
              totalRecords={totalRecords}
              onPageChange={(e) => setCurrentPage(e.page)}
              style={{ marginTop: 10, flexShrink: 0 }}
            />
          )}
        </div>
      )}

      {/* Dialogs */}
      <NewApplicationDialog
        visible={showNewAppDialog}
        onHide={() => { setShowNewAppDialog(false); setSelectedDocument(null); }}
        document={selectedDocument}
        onSuccess={() => invalidateDocumentList(queryClient)}
      />

      <ApplicationDetailsDialog
        visible={showDetailsDialog}
        onHide={() => { setShowDetailsDialog(false); setSelectedDocument(null); }}
        documentId={selectedDocument?.id || null}
      />

      <ChangeAddressDialog
        visible={showChangeAddressDialog}
        onHide={() => { setShowChangeAddressDialog(false); setSelectedDocument(null); }}
        documentId={selectedDocument?.id || null}
        onSuccess={() => {
          invalidateDocumentList(queryClient);
          toast.current?.show({ severity: 'success', summary: 'Success', detail: str('changeAddressSuccess') });
        }}
      />
    </div>
  );
}
